/**
 * Timeline utilities for merging photos and missed captures into a unified timeline
 * Used by Report page for visual timeline grid display
 */

import type { Photo, MissedCapture } from '@/types';
import { formatRelativeDate } from './dateUtils';

/**
 * Timeline slot representing either a received photo or a missed capture
 */
export interface TimelineSlot {
  expectedAt: Date;
  type: 'received' | 'missed';
  photo?: Photo;
  missedCapture?: MissedCapture;
}

/**
 * Timeline row containing slots for grid display
 */
export interface TimelineRow {
  slots: TimelineSlot[];
  startIndex: number;
}

/**
 * Day group with timeline slots organized into rows
 */
export interface TimelineDayGroup {
  dateKey: string;
  displayDate: string;
  slotCount: number;
  receivedCount: number;
  missedCount: number;
  rows: TimelineRow[];
}

/**
 * Complete data structure for timeline grid
 */
export interface TimelineGroupedData {
  groups: TimelineDayGroup[];
  allSlots: TimelineSlot[];
}

/**
 * Format a date as YYYY-MM-DD for comparison/keying
 * Uses local timezone (not UTC) to match display dates
 */
function toDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Convert Firestore Timestamp or Date-like object to Date
 */
function toDate(timestamp: any): Date | null {
  if (!timestamp) return null;
  try {
    if (typeof timestamp.toDate === 'function') {
      return timestamp.toDate();
    }
    if (timestamp instanceof Date) {
      return timestamp;
    }
    if (typeof timestamp.seconds === 'number') {
      return new Date(timestamp.seconds * 1000);
    }
    return new Date(timestamp);
  } catch {
    return null;
  }
}

/**
 * Merge photos and missed captures into a unified timeline
 * Returns slots sorted by expectedAt descending (newest first, like gallery grid)
 *
 * @param photos - Array of received photos
 * @param missedCaptures - Array of missed captures
 * @returns Array of TimelineSlot sorted reverse chronologically (newest first)
 */
export function mergePhotosAndMissed(
  photos: Photo[],
  missedCaptures: MissedCapture[]
): TimelineSlot[] {
  const slots: TimelineSlot[] = [];

  // Add photos as received slots
  for (const photo of photos) {
    const capturedAt = toDate(photo.capturedAt);
    if (capturedAt) {
      slots.push({
        expectedAt: capturedAt,
        type: 'received',
        photo,
      });
    }
  }

  // Add missed captures as missed slots
  for (const missed of missedCaptures) {
    slots.push({
      expectedAt: missed.expectedAt,
      type: 'missed',
      missedCapture: missed,
    });
  }

  // Sort by expectedAt descending (newest first)
  slots.sort((a, b) => b.expectedAt.getTime() - a.expectedAt.getTime());

  return slots;
}

/**
 * Group timeline slots into rows of N columns
 */
function chunkSlotsIntoRows(
  slots: TimelineSlot[],
  columnCount: number,
  globalStartIndex: number
): TimelineRow[] {
  const rows: TimelineRow[] = [];

  for (let i = 0; i < slots.length; i += columnCount) {
    rows.push({
      slots: slots.slice(i, i + columnCount),
      startIndex: globalStartIndex + i,
    });
  }

  return rows;
}

/**
 * Group timeline slots by day and organize into rows for grid display
 * Slots must be pre-sorted by expectedAt (descending - newest first)
 */
export function groupTimelineSlotsForDisplay(
  slots: TimelineSlot[],
  columnCount: number
): TimelineGroupedData {
  if (slots.length === 0) {
    return { groups: [], allSlots: [] };
  }

  const groups: TimelineDayGroup[] = [];
  let currentDateKey: string | null = null;
  let currentSlots: TimelineSlot[] = [];
  let currentStartIndex = 0;

  slots.forEach((slot, index) => {
    const date = slot.expectedAt;
    const dateKey = toDateKey(date);

    if (dateKey !== currentDateKey) {
      // Save previous group if exists
      if (currentDateKey !== null && currentSlots.length > 0) {
        const rows = chunkSlotsIntoRows(currentSlots, columnCount, currentStartIndex);
        const receivedCount = currentSlots.filter((s) => s.type === 'received').length;
        const missedCount = currentSlots.filter((s) => s.type === 'missed').length;
        groups.push({
          dateKey: currentDateKey,
          displayDate: formatRelativeDate(currentSlots[0].expectedAt),
          slotCount: currentSlots.length,
          receivedCount,
          missedCount,
          rows,
        });
      }

      // Start new group
      currentDateKey = dateKey;
      currentSlots = [slot];
      currentStartIndex = index;
    } else {
      // Add to current group
      currentSlots.push(slot);
    }
  });

  // Push last group
  if (currentDateKey !== null && currentSlots.length > 0) {
    const rows = chunkSlotsIntoRows(currentSlots, columnCount, currentStartIndex);
    const receivedCount = currentSlots.filter((s) => s.type === 'received').length;
    const missedCount = currentSlots.filter((s) => s.type === 'missed').length;
    groups.push({
      dateKey: currentDateKey,
      displayDate: formatRelativeDate(currentSlots[0].expectedAt),
      slotCount: currentSlots.length,
      receivedCount,
      missedCount,
      rows,
    });
  }

  return { groups, allSlots: slots };
}

/**
 * Format time for slot display (HH:mm:ss)
 * @param date - The date to format
 * @param timezone - Optional IANA timezone (e.g. "Europe/Rome"). Falls back to browser timezone if undefined.
 */
export function formatSlotTime(date: Date, timezone?: string): string {
  return date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timezone,
  });
}
